<template>
  <div v-if="software" class="d-lg-flex flex-lg-row-reverse">
    <div class="col-lg-3 text-lg-end col-sm-12 pb-4">
      <ShareButton
        :url="shareUrl ?? ''"
        :title="shareTitle ?? ''"
        :share-text="
          $textDictionary['softwareCenter.share.label'] ?? 'Share software'
        "
        :copied-text="
          $textDictionary['downloadCenter.copied.label'] ??
          'Copied to Clipboard'
        "
        :copy-text="
          $textDictionary['downloadCenter.copy.label'] ?? 'Copy download link'
        "
        @click="handleOnShareClick"
      />
    </div>
    <div class="col-lg-9 col-sm-12">
      <h2>{{ software.name }}</h2>
      <MetaList class="mb-4" :items="metaListItems" />
      <div v-if="software.abstract" class="row software-abstract">
        <div
          id="abstractTextHeight"
          :class="showShortedText ? 'rte-text shorted' : 'rte-text'"
          v-html="software.abstract"
        />
        <div v-if="showShortedText" class="button-container text-center pt-3">
          <Button
            class="pt-4"
            style-type="link"
            icon-type="icon-left"
            icon-name="arrow-down"
            @click="showShortedText = false"
          >
            {{
              $textDictionary['downloadCenterSoftware.showFullText'] ??
              'downloadCenterSoftware.showFullText'
            }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Button,
  MetaList,
  ShareButton
} from '@hypercodestudio/basler-components';
import type { SoftwareEntryModel } from '~/lib/DownloadService/model';
import type { MetaItem } from '@hypercodestudio/basler-components/dist/components/elements/MetaList.vue';
import { ref } from 'vue';

const { $textDictionary, $analytics, $globalPageSettings } = useNuxtApp();
const locale = useLocale();

interface Props {
  software?: SoftwareEntryModel | null;
}

const props = defineProps<Props>();

onMounted(() => {
  showShortedText.value = showShortedTextFunc();
});

const showShortedText = ref(false);

const shareUrl = computed(() =>
  props.software?.id
    ? buildUrlString(
        locale.value,
        `${$globalPageSettings.value?.softwareCenterPage?.slug ?? ''}/${
          props.software.id
        }`
      )
    : undefined
);

const shareTitle = computed(() => props.software?.name);
const metaListItems = computed((): MetaItem[] => {
  const items: MetaItem[] = [];
  if (props.software?.dateYear) {
    items.push({
      text: props.software?.dateYear,
      iconName: 'calendar'
    });
  }

  if (props.software?.softwareVersion) {
    items.push({
      text: props.software?.softwareVersion,
      iconName: 'hashtag'
    });
  }

  return items;
});

function handleOnShareClick(): void {
  if (props.software) {
    $analytics?.pushToDataLayer({
      event: 'share',
      ecommerce: {
        method: 'Software Center',
        item_id: `${props.software.id} | ${props.software.name}`
      }
    });
  }
}

function showShortedTextFunc() {
  const element = document.getElementById('abstractTextHeight');
  if (element && element.offsetHeight) {
    return element.offsetHeight > 260;
  }
  return false;
}
</script>

<style scoped>
.software-abstract {
  position: relative;
  .shorted {
    max-height: 260px;
    overflow: hidden;
  }
  .button-container {
    background: rgb(255, 255, 255);
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 1) 50%,
      rgba(255, 255, 255, 1) 100%
    );
    position: absolute;
    bottom: 0;
  }
}
</style>
